import { useContext, createContext, useState, useEffect, useRef } from "react";
import { useMediaDevice } from "@videosdk.live/react-sdk";

export const MeetingAppContext = createContext();

export const useMeetingAppContext = () => useContext(MeetingAppContext);

export const MeetingAppProvider = ({ children }) => {
  const [selectedMic, setSelectedMic] = useState({ id: null, label: null });
  const [selectedWebcam, setSelectedWebcam] = useState({ id: null, label: null });
  const [selectedSpeaker, setSelectedSpeaker] = useState({ id: null, label: null });
  const [isCameraPermissionAllowed, setIsCameraPermissionAllowed] = useState(null);
  const [isMicrophonePermissionAllowed, setIsMicrophonePermissionAllowed] = useState(null);
  const [raisedHandsParticipants, setRaisedHandsParticipants] = useState([]);
  const [sideBarMode, setSideBarMode] = useState(null);
  const [pipMode, setPipMode] = useState(false);

  const { getCameras, getMicrophones, getPlaybackDevices } = useMediaDevice();

  // Initialize devices when permissions are granted
  useEffect(() => {
    const initializeDevices = async () => {
      try {
        if (isCameraPermissionAllowed && !selectedWebcam.id) {
          const cameras = await getCameras();
          if (cameras && cameras.length > 0) {
            setSelectedWebcam({
              id: cameras[0].deviceId,
              label: cameras[0].label || `Camera 1`
            });
          }
        }

        if (isMicrophonePermissionAllowed && !selectedMic.id) {
          const [microphones, speakers] = await Promise.all([
            getMicrophones(),
            getPlaybackDevices()
          ]);
          
          if (microphones && microphones.length > 0) {
            setSelectedMic({
              id: microphones[0].deviceId,
              label: microphones[0].label || `Microphone 1`
            });
          }

          if (speakers && speakers.length > 0) {
            setSelectedSpeaker({
              id: speakers[0].deviceId,
              label: speakers[0].label || `Speaker 1`
            });
          }
        }
      } catch (error) {
        console.error("Error initializing devices:", error);
        // Set fallback values to prevent errors
        if (isCameraPermissionAllowed && !selectedWebcam.id) {
          setSelectedWebcam({ id: "default", label: "Default Camera" });
        }
        if (isMicrophonePermissionAllowed && !selectedMic.id) {
          setSelectedMic({ id: "default", label: "Default Microphone" });
          setSelectedSpeaker({ id: "default", label: "Default Speaker" });
        }
      }
    };

    if (isCameraPermissionAllowed !== null || isMicrophonePermissionAllowed !== null) {
      initializeDevices();
    }
  }, [isCameraPermissionAllowed, isMicrophonePermissionAllowed, getCameras, getMicrophones, getPlaybackDevices, selectedWebcam.id, selectedMic.id]);

  const useRaisedHandParticipants = () => {
    const raisedHandsParticipantsRef = useRef();

    const participantRaisedHand = (participantId) => {
      const raisedHandsParticipants = [...raisedHandsParticipantsRef.current];

      const newItem = { participantId, raisedHandOn: new Date().getTime() };

      const participantFound = raisedHandsParticipants.findIndex(
        ({ participantId: pID }) => pID === participantId
      );

      if (participantFound === -1) {
        raisedHandsParticipants.push(newItem);
      } else {
        raisedHandsParticipants[participantFound] = newItem;
      }

      setRaisedHandsParticipants(raisedHandsParticipants);
    };

    useEffect(() => {
      raisedHandsParticipantsRef.current = raisedHandsParticipants;
    }, [raisedHandsParticipants]);

    const _handleRemoveOld = () => {
      const raisedHandsParticipants = [...raisedHandsParticipantsRef.current];

      const now = new Date().getTime();

      const persisted = raisedHandsParticipants.filter(({ raisedHandOn }) => {
        return parseInt(raisedHandOn) + 15000 > parseInt(now);
      });

      if (raisedHandsParticipants.length !== persisted.length) {
        setRaisedHandsParticipants(persisted);
      }
    };

    useEffect(() => {
      const interval = setInterval(_handleRemoveOld, 1000);

      return () => {
        clearInterval(interval);
      };
    }, []);

    return { participantRaisedHand };
  };

  // Enhanced setters with validation
  const setSelectedMicSafe = (mic) => {
    if (mic && (mic.id || mic.id === "default")) {
      setSelectedMic(mic);
    }
  };

  const setSelectedWebcamSafe = (webcam) => {
    if (webcam && (webcam.id || webcam.id === "default")) {
      setSelectedWebcam(webcam);
    }
  };

  const setSelectedSpeakerSafe = (speaker) => {
    if (speaker && (speaker.id || speaker.id === "default")) {
      setSelectedSpeaker(speaker);
    }
  };

  return (
    <MeetingAppContext.Provider
      value={{
        // states
        raisedHandsParticipants,
        selectedMic,
        selectedWebcam,
        selectedSpeaker,
        sideBarMode,
        pipMode,
        isCameraPermissionAllowed,
        isMicrophonePermissionAllowed,

        // setters
        setRaisedHandsParticipants,
        setSelectedMic: setSelectedMicSafe,
        setSelectedWebcam: setSelectedWebcamSafe,
        setSelectedSpeaker: setSelectedSpeakerSafe,
        setSideBarMode,
        setPipMode,
        useRaisedHandParticipants,
        setIsCameraPermissionAllowed,
        setIsMicrophonePermissionAllowed,
      }}
    >
      {children}
    </MeetingAppContext.Provider>
  );
};
